import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { ButtonTypes, FieldStyles, FieldTypes, ListStyle_Types } from '../../Definitions';
import { colors, css, fonts, glyphs, icons, Units, useMediaQuery, useUnitsComponent } from '../../Styles';
import { Button, ButtonList, FeedCurveViewer, FeedPopover, InputTypes, Modal, Overlay, PopoverTypes, ProgressCircle, SelectField } from '..';

import Last10DaysViewer, { TestData } from "../curveViewer/animalGraph";

import { TestData as FeedCurveData } from '../../Assets/TestData/feedCurvesData';
import { TestData as ComponentData } from '../../Assets/TestData/componentsData';
import { TestData as AnimalData } from '../../Assets/TestData/animalData';

interface Feed_Props {
  selectedAnimal: number
}
/**
 * Feed
 * @param selectedAnimal
 * @returns
 */
export default function Feed({ selectedAnimal }:Feed_Props) {
  const {t} = useTranslation()
  const getUnit = useUnitsComponent();
  const [showFeedViewModal, setFeedViewModal] = useState<boolean>()
  const [showFeedPlan, setShowFeedPlan] = useState<boolean>(false); // "Feed Plan"  
  const [showLastDays, setShowLastDays] = useState<boolean>(false); // "Last 10 Days"
  const [editMode, setEditMode] = useState<boolean>(false)
  const [feedCorrection, setFeedCorrection] = useState<number>(0);

  // #region | Mediaqueries
  const mobile = useMediaQuery(`(max-width: 650px), (min-width: 1060px) and (max-width: 1390px), (min-width: 741px) and (max-width: 1000px)`);
  const mobileXs = useMediaQuery(`(max-width: 450px), (min-width: 1060px) and (max-width: 1150px)`);
  // #endregion
  
  // #region | Data
  // Get animal from selectedAnimal
  const thisAnimal = useMemo(() => {
    return AnimalData.find(animal => animal.animalId === selectedAnimal);
  }, [selectedAnimal]);

  // Animal data
  const totalFood = thisAnimal && thisAnimal.totalFood
  const progressedFood = thisAnimal && thisAnimal.remainingToday
  const feedPlanNo = thisAnimal && thisAnimal.feedPlan

  // FeedCorrection
  useEffect(() => {
    if (thisAnimal?.feedCorrection !== undefined) {
      setFeedCorrection(thisAnimal.feedCorrection);
    } else {
      setFeedCorrection(0);
    }
  }, [thisAnimal]);
  
  // Feedplan data
  const feedPlan = useMemo(() => {
    return FeedCurveData.find((curve: { id: number | undefined; }) => curve.id === feedPlanNo);
  }, [feedPlanNo]);

  const feedComponent = ComponentData;

  const feedCurve = <>
    {feedPlan ? (
      <FeedCurveViewer data={feedPlan} feedComponents={feedComponent} showComponentLabels={true} showMilestoneLabels={true}/>
    ) : (
      <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary,}}>No feed curve found</p> // Returner en tom div, hvis der ikke er nogen feedPlan
    )}
  </>

  const lastDays = 
  <div style={{marginTop: "20px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px"}}>
    <Last10DaysViewer data={TestData}/>
  </div> 
  // #endregion

  // #region | Info box
  const info_feedPlan = 
  <div style={{...css.basic_Info_style, gridTemplateColumns: "7fr 25px 8fr", margin: "0px"}}>
    <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("feed.feedPlan.feedPlan")}</div>
    <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.FeedPlan size='25px'/></div>
    <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editMode ? "5px" : "10px"}}>
      { editMode
        ? <SelectField
            defaultValue={feedPlan?.name} 
            options={FeedCurveData.map((feedCurve) => ({label: feedCurve.name, id: feedCurve.id}))} 
            overrides={{
              fieldStyle: FieldStyles.greySecondary,
              fieldType: FieldTypes.editField
            }}
          />
        : <>{feedPlan?.name}</> 
      }
    </div>
  </div>

  const info_corrections = 
  <div style={{...css.basic_Info_style, gridTemplateColumns: "7fr 25px 8fr", margin: "0px"}}>
    <div style={{gridArea: "1/1/2/2", ...css.basicInfo_rightStyle}}>{t("feed.feedCurve.corrections")}</div>
    <div style={{gridArea: "1/2/2/3", ...css.basicInfo_centerStyle}}><glyphs.FeedPlan size='25px'/></div>
    <div style={{gridArea: "1/3/2/4", ...css.basicInfo_leftStyle, marginLeft: editMode ? "5px" : "10px"}}>
    { editMode 
      ? <FeedPopover
          popoverType={PopoverTypes.feedCorrection}
          inputType={InputTypes.Number}
          closeOnBtnClick={[false, false, true]}
          header={t("feed.feedCorrections.feedCorrection")}
          input={feedCorrection ? feedCorrection+"%" : "0%"}
          content={[glyphs.Minus({fillColor: colors.greyLightTertiary}), glyphs.Add({fillColor: colors.greyLightTertiary}), t("buttons.reset")]}
          onClick={(i => {
            if (i === 0) setFeedCorrection((feedCorrection ?? 0) -1);
            else if (i === 1) setFeedCorrection((feedCorrection?? 0) +1);
            else setFeedCorrection(0);
          })}
          overrides={{
            value: {backgroundColor: colors.greySecondary, ...fonts.fontSize3, color: colors.greyLightTertiary}
          }}
        />
      : <>{feedCorrection+"%"}</>
    }
    </div>
  </div>
  // #endregion

  // #region | Feed graphs
  const feedGraphsBtns = (
    <ButtonList contents={[t("feed.feedPlan.feedPlan"), t("locationFeeder.penPopup.lastDays")]} 
      overrides={{
        baseButton: {...css.button_FitContent}
      }}
      onClick={(i) => {
        if (i === 0) { setFeedViewModal(true); setShowFeedPlan(true) }
        if (i === 1) { setFeedViewModal(true); setShowLastDays(true) }
      }}
      listOverrides={{
        listStyle: ListStyle_Types.horizontal
      }}
    />
  )

  const feedGraphsModals = (
    <Modal isModalOpen={!!showFeedViewModal}
      onCloseBtn={() => {setFeedViewModal(false); setShowFeedPlan(false); setShowLastDays(false)}}
      overrides={{
        isPrimaryComponent: true,
        root: { padding: "40px 5px 5px 5px" },
        background: colors.greyLightTertiary
      }}
      modalHeader={showFeedPlan ? 'FeedCurve' : "Last days"}
      modalSubHeader={`Animal No. ${thisAnimal?.animalId}`}
      modalHeaderContent={
        <div style={{display: "flex", justifyContent: "center"}}>
          <Button 
            content={showFeedPlan ? "Watch last days" : "Watch feed plan"} 
            onClick={() => setShowFeedPlan(!showFeedPlan)}
            buttonType={ButtonTypes.textButton}
            startEnhancer={showFeedPlan ? <></> : glyphs.Arrow({rotate: 180})}
            endEnhancer={showFeedPlan ? glyphs.Arrow({}) : <></>}
            overrides={{
              baseButton: {...fonts.fontSize4}
            }}
          />
        </div>
      }
    >
      <div style={{overflowX: "scroll", scrollbarColor: colors.greyTertiary+colors.transparent, scrollbarWidth: "thin", backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 10px 10px", padding: "0px 20px 30px 20px"}}>
        <div style={{width: "fit-content"}}>{showFeedPlan ? <>{feedCurve}</> : <>{lastDays}</>}</div>
      </div>
    </Modal>
  )
  // #endregion

  return ( <>
    { editMode ? <Overlay color={colors.transparent} zIndex={10}/> : <></> }
    <div style={{position: "relative", height: "calc(100% - 50px)"}}>
      {/* <div style={{ position: "absolute", zIndex: "15", right: "0", top: "-50px", backgroundColor: "transparent", display: "flex", justifyContent: "end", padding: "0px 20px", gap: "5px"}}> */}
      <div style={{display: "flex", position: "relative", justifyContent: "end", padding: "0px 20px", zIndex: editMode? "9999" : "0"}}>
        { editMode ?
          <ButtonList
            contents={[t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.edit")]}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[
              glyphs.Check({}), 
              glyphs.Close({}),
              glyphs.Edit({})
            ]}
            onClick={(i => {
              if (i === 0) {} // TODO: Save to db
              if (i === 1) {setEditMode(false)}
            })}
            disabled={[false, false, true]}
            overrides={(index) => ({
              baseButton: {backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary},
              hoverStyles: {backgroundColor: colors.greyPrimary}
            })}
          /> : 
          <Button 
            content={t("buttons.edit")}
            buttonType={ButtonTypes.tableButton} 
            startEnhancer={glyphs.Edit({})} 
            onClick={() => setEditMode(true)}
          />
        }
      </div>
      <div style={{
        backgroundColor: colors.greyLightTertiary, 
        border: "5px solid"+colors.greyPrimary, 
        borderRadius: "10px", 
        display: "grid", 
        gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", 
        gridTemplateRows: mobile ? "1fr 1fr" : "1fr", 
        height: "100%", 
        boxSizing: "border-box",
        overflowY: mobile ? "scroll" : "hidden", 
        scrollbarColor: colors.greySecondary+colors.greyPrimary, 
        scrollbarWidth: "thin"
      }}>
        <div style={{gridArea: "1/1/2/2", padding: "10px", display: "flex", alignItems: "start", justifyContent: "center"}}>
          <div style={{ ...css.modalTop, alignItems: "end", gap: mobileXs ? "40px" : "60px"}}>
            {/* Fed Today */}
            <ProgressCircle header={t("feed.fed.today")}
              total={totalFood ?? 0} 
              progressed={progressedFood ?? 0} 
              unit={getUnit(Units.kG_grey)} 
            />
            {/* Fed During Period */}
            <ProgressCircle header={t("feed.fed.period")} 
              total={totalFood ?? 0} 
              progressed={progressedFood ?? 0}
              unit={getUnit(Units.kG_grey)}
            />
          </div>
        </div>
        <div style={{gridArea: mobile ? "2/1/3/2" : "1/2/2/3", padding: "10px", display: "flex", gap: "20px", flexDirection: "column", alignItems: "center", justifyContent: "space-between", backgroundColor: colors.greyPrimary}}>
          <div style={{zIndex: "10", width: "270px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "10px" }}>
            {info_feedPlan}
            {info_corrections}
          </div>
          <div style={{width: "80%", margin: "0 auto"}}>
            <div style={{height: "1px", backgroundColor: colors.greySecondary, marginBottom: "20px"}}/>
            <div style={{display: "flex", justifyContent: "center"}}>{feedGraphsBtns}</div>
            {feedGraphsModals}
          </div>
        </div>
      </div>
    </div>
  </> );
} 